import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { distinctUntilChanged, finalize, forkJoin, Subscription } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { DropdownOption, PKL_GRP } from '../../../core/shared/models/picklist.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { ToastService } from '../../../core/shared/services/toast.service';

import {
  CalculateDisbursementRepaymentPlanItemPayload,
  CalculateDisbursementRepaymentPlanPayload,
  CalculateDisbursementRepaymentPlanTermPayload,
  CraditService,
} from '../shared/services/cradit.service';

import {
  extractResultColumns,
  extractResultRows,
  extractResultSummary,
  formatColumnLabel,
  formatResultValue,
  ResultSummaryItem,
} from '../shared/utils/repayment-api.util';
import { RepaymentPlanStateService } from '../disbursement-repayment/repayment/repayment-plan-state.service';
import { blockManualDateEntry as blockManualDateEntryUtil } from '../../../core/shared/dynamic-form/date-input.utils';

interface CalculateRepaymentPlanConfig {
  calculatedPlan?: unknown;
  [key: string]: unknown;
}

interface DisbursementSummaryRow {
  prpsdDsbmntSeq: number | null;
  schMth: string;
  prpsdDsbmntAmt: number;
  /** Repayment term type (PKL_GRP.FACILITY_TYPE) this row belongs to — drives the table it's grouped under. */
  termRefCdSeq: number;
}

interface DisbursementRowGroup {
  termRefCdSeq: number;
  title: string;
  total: number;
  rows: { row: DisbursementSummaryRow; number: number; index: number }[];
}

@Component({
  selector: 'app-calculate-repayment-plan',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  templateUrl: './calculate-repayment-plan.html',
  styleUrl: './calculate-repayment-plan.css',
})
export class CalculateRepaymentPlan implements OnInit, OnChanges, OnDestroy {
  @Input() config: CalculateRepaymentPlanConfig | null = null;
  @Input() editable = false;
  @Input() crdtPrpslSeq: number | null = null;
  @Input() sectionId = '';

  readonly frequencyGroup = PKL_GRP.DISBURSEMENT_FREQUENCY;
  readonly blockManualDateEntry = blockManualDateEntryUtil;
  frequencyOptions: DropdownOption[] = [];
  termOptions: DropdownOption[] = [];

  gracePeriod = 0;
  numberOfInstallments = 0;
  arrearFlg = 0;
  frequency: number | null = null;

  prevMaxExposure = 0;
  disbursementRows: DisbursementSummaryRow[] = [];

  loadingInputs = false;
  calculating = false;

  loadErrorMessage = '';
  errorMessage = '';
  successMessage = '';

  resultRows: Record<string, unknown>[] = [];
  resultColumns: string[] = [];
  resultSummary: ResultSummaryItem[] = [];

  private initialized = false;
  private loadedForSequence: number | null = null;

  private readonly subscriptions = new Subscription();

  constructor(
    private creditService: CraditService,
    private pickListService: PickListService,
    private repaymentPlanState: RepaymentPlanStateService,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.initialized = true;

    this.loadFrequencyOptions();
    this.loadTermOptions();

    const sequenceSubscription = this.creditService.crdtPrpslSeq$
      .pipe(distinctUntilChanged())
      .subscribe((sequence) => {
        const normalizedSequence = Number(sequence ?? 0);

        if (!Number.isFinite(normalizedSequence) || normalizedSequence <= 0) {
          return;
        }

        this.crdtPrpslSeq = normalizedSequence;
        this.loadInputs(normalizedSequence);
      });

    this.subscriptions.add(sequenceSubscription);

    const saveTriggerSubscription = this.creditService.saveSectionTrigger$.subscribe(
      (sectionId) => {
        if (sectionId === this.sectionId) {
          this.calculateRepaymentPlan();
        }
      },
    );

    this.subscriptions.add(saveTriggerSubscription);

    const currentSequence = Number(this.crdtPrpslSeq ?? this.creditService.getCrdtPrpslSeq() ?? 0);

    if (currentSequence > 0) {
      this.crdtPrpslSeq = currentSequence;
      this.loadInputs(currentSequence);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes['crdtPrpslSeq']) {
      const sequence = Number(this.crdtPrpslSeq ?? 0);

      if (sequence > 0) {
        this.loadInputs(sequence);
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get totalDisbursementAmount(): number {
    return this.disbursementRows.reduce((total, row) => {
      const amount = Number(row.prpsdDsbmntAmt ?? 0);
      return Number.isFinite(amount) ? total + amount : total;
    }, 0);
  }

  get proposedMaxExposure(): number {
    return this.prevMaxExposure + this.totalDisbursementAmount;
  }

  // Splits the flat disbursement row list into one table per repayment
  // term (e.g. Islamic Finance, Terms Finance) — mirrors the grouping the
  // Repayment Plan builder uses for `groupedDisplayDisbursements`.
  get groupedDisbursementRows(): DisbursementRowGroup[] {
    const groups = new Map<number, { row: DisbursementSummaryRow; index: number }[]>();

    this.disbursementRows.forEach((row, index) => {
      const key = Number(row.termRefCdSeq ?? 0);
      const entries = groups.get(key) ?? [];

      entries.push({ row, index });
      groups.set(key, entries);
    });

    return Array.from(groups.entries()).map(([termRefCdSeq, entries]) => ({
      termRefCdSeq,
      title: this.getDisbursementGroupTitle(termRefCdSeq),
      total: entries.reduce((sum, entry) => sum + Number(entry.row.prpsdDsbmntAmt ?? 0), 0),
      rows: entries.map((entry, position) => ({
        row: entry.row,
        number: position + 1,
        index: entry.index,
      })),
    }));
  }

  get selectedFrequencyLabel(): string {
    if (this.frequency === null || this.frequency === undefined) {
      return '-';
    }

    const selectedOption = this.frequencyOptions.find(
      (option) => Number(option.value) === Number(this.frequency),
    );

    return selectedOption?.label ?? String(this.frequency);
  }

  refreshInputs(): void {
    const proposalSequence = Number(this.crdtPrpslSeq ?? this.creditService.getCrdtPrpslSeq() ?? 0);

    if (proposalSequence <= 0) {
      this.loadErrorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.SEQ_NA');

      return;
    }

    this.loadInputs(proposalSequence, true);
  }

  calculateRepaymentPlan(): void {
    if (!this.editable) {
      return;
    }

    this.errorMessage = '';
    this.successMessage = '';

    const proposalSequence = Number(this.crdtPrpslSeq ?? this.creditService.getCrdtPrpslSeq() ?? 0);

    if (!Number.isFinite(proposalSequence) || proposalSequence <= 0) {
      this.errorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.SEQ_REQUIRED');

      this.reportValidationError(this.errorMessage);
      return;
    }

    if (this.frequency === null || !Number.isFinite(this.frequency)) {
      this.errorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.FREQUENCY_NA');

      this.reportValidationError(this.errorMessage);
      return;
    }

    if (this.disbursementRows.length === 0) {
      this.errorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.NO_DISBURSEMENT_ROWS');

      this.reportValidationError(this.errorMessage);
      return;
    }

    const invalidRow = this.disbursementRows.find(
      (row) =>
        !row.schMth ||
        !Number.isFinite(Number(row.prpsdDsbmntAmt)) ||
        Number(row.prpsdDsbmntAmt) <= 0,
    );

    if (invalidRow) {
      this.errorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.INVALID_ROWS');

      this.reportValidationError(this.errorMessage);
      return;
    }

    const payload: CalculateDisbursementRepaymentPlanPayload = {
      crdtPrpslSeq: proposalSequence,
      gracePeriod: this.gracePeriod,
      numberOfInstallments: this.numberOfInstallments,
      arrear: this.arrearFlg,
      frequency: this.frequency,
      prpsdMaxExpsr: this.proposedMaxExposure,
      totalAmount: this.totalDisbursementAmount,
      prevMaxExposure: this.prevMaxExposure,
      terms: this.groupDisbursementsByTerm(this.disbursementRows),
    };

    this.calculating = true;
    this.clearResult();

    const calculateSubscription = this.creditService
      .calculateDisbursementRepaymentPlan(payload)
      .pipe(finalize(() => (this.calculating = false)))
      .subscribe({
        next: (response: unknown) => {
          this.resultRows = extractResultRows(response);
          this.resultColumns = extractResultColumns(this.resultRows);
          this.resultSummary = extractResultSummary(response);

          const calculatedPlan = this.repaymentPlanState.publishCalculationResponse(response);

          if (!calculatedPlan) {
            this.errorMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.INVALID_RESPONSE');

            this.creditService.reportSectionSaveResult({
              sectionId: this.sectionId,
              status: 'api-error',
              message: this.errorMessage,
            });
            return;
          }

          this.successMessage = this.translate.instant('CREDIT_REPAYMENT_CALC.CALC_SUCCESS');

          this.toast.success(this.successMessage);

          if (this.config) {
            this.config.calculatedPlan = calculatedPlan;
          }

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'saved',
          });
        },

        error: (error: any) => {
          this.errorMessage =
            error?.error?.message ?? this.translate.instant('CREDIT_REPAYMENT_CALC.CALC_FAILED');

          console.error('Calculate Repayment Plan API error:', error);

          this.creditService.reportSectionSaveResult({
            sectionId: this.sectionId,
            status: 'api-error',
            message: this.errorMessage,
          });
        },
      });

    this.subscriptions.add(calculateSubscription);
  }

  private reportValidationError(message: string): void {
    this.creditService.reportSectionSaveResult({
      sectionId: this.sectionId,
      status: 'validation-error',
      message,
    });
  }

  clearResult(): void {
    this.resultRows = [];
    this.resultColumns = [];
    this.resultSummary = [];
  }

  formatColumnLabel(value: string): string {
    return formatColumnLabel(value);
  }

  formatResultValue(value: unknown): string {
    if (typeof value === 'boolean') {
      return this.translate.instant(value ? 'COMMON.YES' : 'COMMON.NO');
    }

    return formatResultValue(value);
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByColumn(index: number, column: string): string {
    return column;
  }

  private getTermLabel(termRefCdSeq: number): string {
    if (!termRefCdSeq) {
      return '';
    }

    const option = this.termOptions.find(
      (item) => Number(item.value) === Number(termRefCdSeq),
    );

    return option?.label ? String(option.label).trim() : '';
  }

  private getDisbursementGroupTitle(termRefCdSeq: number): string {
    const label = this.getTermLabel(termRefCdSeq);

    return label
      ? this.translate.instant('CREDIT_REPAYMENT_CALC.TERM_PROPOSED_DISBURSEMENTS', { term: label })
      : this.translate.instant('CREDIT_REPAYMENT_CALC.PROPOSED_DISBURSEMENTS');
  }

  private loadTermOptions(): void {
    const termsSubscription = this.pickListService
      .loadCodesAsync([PKL_GRP.FACILITY_TYPE])
      .subscribe({
        next: () => {
          // Repayment term types come from ref code group 13 (FACILITY_TYPE).
          this.termOptions = [
            ...this.pickListService.getOptionsSync(PKL_GRP.FACILITY_TYPE),
          ].sort(
            (first, second) =>
              Number(first.original?.sortOrder ?? 0) - Number(second.original?.sortOrder ?? 0),
          );
        },

        error: (error: unknown) => {
          console.error('Failed to load repayment terms:', error);
        },
      });

    this.subscriptions.add(termsSubscription);
  }

  private loadFrequencyOptions(): void {
    const frequencySubscription = this.pickListService.getOptions(this.frequencyGroup).subscribe({
      next: (options: DropdownOption[]) => {
        this.frequencyOptions = (Array.isArray(options) ? options : [])
          .filter(
            (option) =>
              option.original?.refCdActvFlg !== false && Number(option.original?.delFlg ?? 0) !== 1,
          )
          .map(
            (option): DropdownOption => ({
              ...option,
              value: Number(option.value),
            }),
          );
      },

      error: (error: unknown) => {
        console.error('Failed to load disbursement frequency:', error);
      },
    });

    this.subscriptions.add(frequencySubscription);

    this.pickListService.loadCodes([this.frequencyGroup]);
  }

  private loadInputs(sequence: number, forceReload = false): void {
    if (!forceReload && this.loadedForSequence === sequence) {
      return;
    }

    this.loadingInputs = true;
    this.loadErrorMessage = '';

    const loadSubscription = forkJoin({
      terms: this.creditService.getCreditTermParameters(sequence),
      exposure: this.creditService.getPreviousMaxExposure(sequence),
      disbursements: this.creditService.getProposedDisbursements(sequence),
    })
      .pipe(finalize(() => (this.loadingInputs = false)))
      .subscribe({
        next: ({ terms, exposure, disbursements }) => {
          const termsSummary = this.extractTermsSummary(terms);

          this.gracePeriod = termsSummary?.grcPrd ?? 0;
          this.numberOfInstallments = termsSummary?.noOfInstlmnt ?? 0;
          this.frequency = termsSummary?.rpymntFrqncy ?? null;
          this.arrearFlg = termsSummary?.arrearFlg ?? 0;

          this.prevMaxExposure = this.extractExposure(exposure);
          this.disbursementRows = this.extractDisbursementRows(disbursements);

          this.loadedForSequence = sequence;
        },

        error: (error: any) => {
          console.error('Unable to load Repayment Plan inputs:', error);

          this.loadErrorMessage =
            error?.error?.message ?? this.translate.instant('CREDIT_REPAYMENT_CALC.LOAD_FAILED');
        },
      });

    this.subscriptions.add(loadSubscription);
  }

  private extractTermsSummary(response: any): {
    grcPrd: number;
    noOfInstlmnt: number;
    rpymntFrqncy: number;
    arrearFlg: number;
  } | null {
    const source =
      response?.data && typeof response.data === 'object' && !Array.isArray(response.data)
        ? response.data
        : response;

    if (!source || typeof source !== 'object') {
      return null;
    }

    return {
      grcPrd: Number(source.grcPrd ?? 0),
      noOfInstlmnt: Number(source.noOfInstlmnt ?? 0),
      rpymntFrqncy: Number(source.rpymntFrqncy ?? 0),
      arrearFlg: Number(source.arrearFlg) === 1 ? 1 : 0,
    };
  }

  private extractExposure(response: any): number {
    const firstArrayItem = Array.isArray(response) ? response[0] : undefined;

    const candidates: unknown[] = [
      response,
      response?.baseExposure,
      response?.maxExposure,
      response?.prvsMaxExpsr,
      response?.value,
      response?.data,
      response?.data?.baseExposure,
      response?.data?.maxExposure,
      response?.data?.prvsMaxExpsr,
      response?.data?.value,
      firstArrayItem?.baseExposure,
      firstArrayItem?.maxExposure,
      firstArrayItem?.value,
    ];

    for (const candidate of candidates) {
      if (candidate === null || candidate === undefined || candidate === '') {
        continue;
      }

      if (typeof candidate === 'object') {
        continue;
      }

      const numericValue = Number(candidate);

      if (Number.isFinite(numericValue) && numericValue >= 0) {
        return numericValue;
      }
    }

    return 0;
  }

  private extractDisbursementRows(response: any): DisbursementSummaryRow[] {
    const list = Array.isArray(response)
      ? response
      : Array.isArray(response?.prpsdDsbmntRowDTOList)
        ? response.prpsdDsbmntRowDTOList
        : Array.isArray(response?.data?.prpsdDsbmntRowDTOList)
          ? response.data.prpsdDsbmntRowDTOList
          : Array.isArray(response?.data)
            ? response.data
            : [];

    return list.map((item: any) => ({
      prpsdDsbmntSeq: this.toNullableSequence(item?.prpsdDsbmntSeq),
      schMth: String(item?.schMth ?? '').slice(0, 10),
      prpsdDsbmntAmt: Number(item?.prpsdDsbmntAmt ?? 0),
      termRefCdSeq: Number(item?.rpymntTerm ?? item?.typ ?? item?.termRefCdSeq ?? 0),
    }));
  }

  /**
   * `0`, missing, or non-positive values all mean "not persisted as a
   * disbursement yet" and must be sent as `null`, never `0` — the API
   * payload distinguishes an existing row (its real sequence) from a new
   * one (`null`) by that value alone.
   */
  private toNullableSequence(value: unknown): number | null {
    const numeric = Number(value);

    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
  }

  // Groups disbursement rows by their repayment term into the `terms[]`
  // shape the calculate API expects.
  private groupDisbursementsByTerm(
    rows: DisbursementSummaryRow[],
  ): CalculateDisbursementRepaymentPlanTermPayload[] {
    const groups = new Map<number, CalculateDisbursementRepaymentPlanItemPayload[]>();

    rows.forEach((row) => {
      const key = Number(row.termRefCdSeq ?? 0);
      const list = groups.get(key) ?? [];

      list.push({
        prpsdDsbmntSeq: this.toNullableSequence(row.prpsdDsbmntSeq),
        schMth: row.schMth,
        prpsdDsbmntAmt: Number(row.prpsdDsbmntAmt),
      });

      groups.set(key, list);
    });

    return Array.from(groups.entries()).map(([rpymntTerm, disbursements]) => ({
      rpymntTerm,
      disbursements,
    }));
  }
}
